import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { X, ShieldCheck, Car, Lock } from 'lucide-react';
import { Button } from '../Button';
import * as agenda from '../../services/agendaService';
import type { BlokkadeSoort, LocatieType, MatrixKleur } from '../../services/agendaService';
import {
  B2B_SOORT_LABEL,
  getB2bWerkgevers,
  planB2bAfspraakAlsProfessional,
  type B2bLocatie,
  type B2bSoort,
} from '../../services/lopendeTrajectenApi';
import { KLEUR_STAAL } from './kleuren';

/**
 * Pop-up na het trekken van een periode: [ ➕ Wat wilt u inplannen voor deze
 * periode? ]. De gekozen optie kleurt het getrokken blok direct mee.
 */

export type PlanOptie = 'beschikbaar' | 'medewerker' | 'corporate' | 'netwerk' | 'afwezig' | 'prive';

const OPTIE_KLEUR: Record<PlanOptie, MatrixKleur> = {
  beschikbaar: 'mint',
  medewerker: 'blauw',
  corporate: 'goud',
  netwerk: 'mos',
  afwezig: 'zand',
  prive: 'paars',
};

/** Wekelijks herhalen: zoveel kalenderweken vooruit. */
export const HERHAAL_WEKEN = 24;

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy-500 focus:border-navy-500 disabled:bg-gray-50 disabled:text-gray-400';
const lokaal = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm");
const fout = (err: unknown, standaard: string) => (isAxiosError(err) ? err.response?.data?.message : undefined) ?? standaard;

const LOCATIE_SESSIE: LocatieType[] = ['ONLINE', 'WERKGEVER', 'EXTERN'];
const LOCATIE_B2B: B2bLocatie[] = ['online', 'telefonisch', 'op_locatie'];

interface PlanPopupProps {
  start: Date;
  eind: Date;
  onKleur: (kleur: MatrixKleur | null) => void;
  onPeriode: (start: Date, eind: Date) => void;
  onSluiten: () => void;
  onGepland: () => void;
}

export const PlanPopup: React.FC<PlanPopupProps> = ({ start, eind, onKleur, onPeriode, onSluiten, onGepland }) => {
  const { t } = useTranslation('agenda');
  const [optie, setOptie] = useState<PlanOptie | null>(null);
  const [van, setVan] = useState(lokaal(start));
  const [tot, setTot] = useState(lokaal(eind));

  useEffect(() => {
    onKleur(optie ? OPTIE_KLEUR[optie] : null);
  }, [optie]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sluit = (e: KeyboardEvent) => e.key === 'Escape' && onSluiten();
    window.addEventListener('keydown', sluit);
    return () => window.removeEventListener('keydown', sluit);
  }, [onSluiten]);

  const zetPeriode = (nieuwVan: string, nieuwTot: string) => {
    setVan(nieuwVan);
    setTot(nieuwTot);
    const s = new Date(nieuwVan);
    const e = new Date(nieuwTot);
    if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e > s) onPeriode(s, e);
  };

  const periodeGeldig = new Date(tot) > new Date(van);
  const opties: Array<{ key: PlanOptie; label: string; uitleg: string }> = [
    { key: 'beschikbaar', label: t('matrix.optie.beschikbaar', 'Beschikbaar tijdslot'), uitleg: t('matrix.optie.beschikbaarUitleg', 'Inloop-opening die medewerkers in je pools kunnen claimen') },
    { key: 'medewerker', label: t('matrix.optie.medewerker', 'Medewerker-sessie'), uitleg: t('matrix.optie.medewerkerUitleg', 'Vervolgsessie op een lopend traject') },
    { key: 'corporate', label: t('matrix.optie.corporate', 'Corporate B2B-overleg'), uitleg: t('matrix.optie.corporateUitleg', 'Overleg met HR, casemanager of een externe genodigde') },
    { key: 'netwerk', label: t('matrix.optie.netwerk', 'Netwerk / evenement'), uitleg: t('matrix.optie.netwerkUitleg', 'Netwerklunch, klantevenement of relatiebijeenkomst') },
    { key: 'afwezig', label: t('matrix.optie.afwezig', 'Vakantie / afwezigheid'), uitleg: t('matrix.optie.afwezigUitleg', 'Inloop sluit; medewerkers gaan naar collega’s in de pool') },
    { key: 'prive', label: t('matrix.optie.prive', 'Privé-afspraak'), uitleg: t('matrix.optie.priveUitleg', 'Anderen zien alleen ‘niet beschikbaar’') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onSluiten}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('matrix.popup.titel', '➕ Wat wilt u inplannen voor deze periode?')}</h3>
          <button type="button" onClick={onSluiten} className="p-1 rounded hover:bg-gray-100" aria-label={t('plan.sluiten', 'Sluiten')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">{t('matrix.popup.van', 'Van')}</span>
              <input type="datetime-local" className={inputClass} value={van} onChange={(e) => zetPeriode(e.target.value, tot)} />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">{t('matrix.popup.tot', 'Tot')}</span>
              <input type="datetime-local" className={inputClass} value={tot} onChange={(e) => zetPeriode(van, e.target.value)} />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {opties.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setOptie(o.key)}
                className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${optie === o.key ? 'border-navy-700 bg-navy-50 ring-1 ring-navy-700' : 'border-gray-200 hover:border-navy-300'}`}
              >
                <span className={`mt-0.5 w-3.5 h-3.5 rounded-sm border flex-shrink-0 ${KLEUR_STAAL[OPTIE_KLEUR[o.key]]}`} />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{o.label}</span>
                  <span className="block text-xs text-gray-500">{o.uitleg}</span>
                </span>
              </button>
            ))}
          </div>

          {!periodeGeldig && <p className="text-xs text-red-600">{t('week.ongeldigeTijd', 'De eindtijd moet na de starttijd liggen.')}</p>}

          {optie === 'beschikbaar' && <BeschikbaarFormulier van={van} tot={tot} geldig={periodeGeldig} onKlaar={onGepland} />}
          {optie === 'medewerker' && <MedewerkerFormulier van={van} onKlaar={onGepland} />}
          {optie === 'corporate' && <CorporateFormulier van={van} tot={tot} geldig={periodeGeldig} onKlaar={onGepland} />}
          {(optie === 'netwerk' || optie === 'afwezig' || optie === 'prive') && (
            <BlokkadeFormulier
              soort={optie === 'netwerk' ? 'NETWERK' : optie === 'prive' ? 'PRIVE' : 'VAKANTIE'}
              van={van}
              tot={tot}
              geldig={periodeGeldig}
              onKlaar={onGepland}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Beschikbaar tijdslot (mint) met de recurrentie-grendel ───────────────────

const BeschikbaarFormulier: React.FC<{ van: string; tot: string; geldig: boolean; onKlaar: () => void }> = ({ van, tot, geldig, onKlaar }) => {
  const { t } = useTranslation('agenda');
  const [herhalen, setHerhalen] = useState(false);
  const open = useMutation({
    mutationFn: () => {
      const s = new Date(van);
      const e = new Date(tot);
      const weken = herhalen ? HERHAAL_WEKEN : 1;
      const blokken = Array.from({ length: weken }, (_, w) => {
        const bs = new Date(s);
        const be = new Date(e);
        bs.setDate(bs.getDate() + w * 7);
        be.setDate(be.getDate() + w * 7);
        return { start: bs.toISOString(), end: be.toISOString() };
      });
      return agenda.openSlots(blokken);
    },
    onSuccess: (data) => {
      toast.success(t('slots.opengezet', { count: data.aangemaakt, defaultValue: '{{count}} tijdslot(s) opengezet' }));
      onKlaar();
    },
    onError: (err) => toast.error(fout(err, t('fout.openzetten', 'Openzetten mislukt'))),
  });

  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      <fieldset className="space-y-1.5 text-sm">
        <label className="flex items-center gap-2">
          <input type="radio" name="herhaal" checked={!herhalen} onChange={() => setHerhalen(false)} />
          {t('matrix.herhaal.eenmalig', 'Alleen nu voor deze specifieke week toepassen')}
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name="herhaal" checked={herhalen} onChange={() => setHerhalen(true)} />
          {t('matrix.herhaal.wekelijks', { defaultValue: 'Automatisch herhalen voor de komende {{weken}} weken (vaste wekelijkse beschikbaarheid)', weken: HERHAAL_WEKEN })}
        </label>
      </fieldset>
      <p className="text-xs text-gray-500">
        {t('matrix.herhaal.uitleg', 'Tijd die al bezet of privé geblokkeerd is, blijft dicht. Losse weken kun je daarna per blok sluiten.')}
      </p>
      <div className="flex justify-end">
        <Button onClick={() => open.mutate()} loading={open.isPending} disabled={!geldig}>
          {t('matrix.opslaan.beschikbaar', 'Tijd openzetten')}
        </Button>
      </div>
    </div>
  );
};

// ── Actie-pad A: lopende medewerkerstrajecten (blauw) ────────────────────────

const MedewerkerFormulier: React.FC<{ van: string; onKlaar: () => void }> = ({ van, onKlaar }) => {
  const { t } = useTranslation('agenda');
  const { data: dossiers = [], isLoading } = useQuery({ queryKey: ['agenda-dossiers'], queryFn: agenda.getAgendaDossiers });
  const planbaar = dossiers.filter((d) => !d.plafond_bereikt);
  const [dossierId, setDossierId] = useState('');
  const [locatie, setLocatie] = useState<LocatieType>('ONLINE');
  const [omschrijving, setOmschrijving] = useState('');
  const gekozen = planbaar.find((d) => d.id === dossierId) ?? null;

  const plan = useMutation({
    mutationFn: () =>
      agenda.planMatrixSessie({
        dossier_id: dossierId,
        start_tijd: new Date(van).toISOString(),
        locatie_type: locatie,
        locatie_omschrijving: omschrijving.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('matrix.gepland.medewerker', 'Sessie voorgesteld — wacht op bevestiging van de medewerker'));
      onKlaar();
    },
    onError: (err) => toast.error(fout(err, t('fout.boeken', 'Inplannen mislukt'))),
  });

  if (isLoading) return <p className="text-sm text-gray-500">{t('matrix.laden', 'Laden…')}</p>;
  if (planbaar.length === 0) return <p className="text-sm text-gray-600 border-t border-gray-100 pt-3">{t('plan.geenDossiers', 'Geen traject beschikbaar om in te plannen.')}</p>;

  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      <label className="block text-sm">
        <span className="text-gray-700">{t('matrix.padA.dossier', '🔍 Selecteer actieve medewerker of casus')}</span>
        <select className={`${inputClass} font-mono`} value={dossierId} onChange={(e) => setDossierId(e.target.value)}>
          <option value="">{t('matrix.padA.kies', '— kies een casus —')}</option>
          {planbaar.map((d) => (
            <option key={d.id} value={d.id}>
              {d.client_registration_number || d.id.slice(0, 8)} · {t(`sessieType.${d.volgende_sessie_type}`, d.volgende_sessie_type === 'intake' ? 'Intake' : 'Vervolggesprek')}
            </option>
          ))}
        </select>
      </label>
      {gekozen && (
        <p className="text-xs text-gray-600">
          {t('matrix.padA.duur', {
            defaultValue: 'Starttijd {{start}}; de duur volgt uit je discipline: {{duur}} minuten.',
            start: format(new Date(van), 'HH:mm'),
            duur: gekozen.duur_minuten,
          })}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="text-gray-700">{t('plan.locatie', 'Locatie')}</span>
          <select className={inputClass} value={locatie} onChange={(e) => setLocatie(e.target.value as LocatieType)}>
            {LOCATIE_SESSIE.map((l) => (
              <option key={l} value={l}>{t(`locatie.${l}`, l)}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">{t('matrix.padA.omschrijving', 'Locatie-omschrijving')}</span>
          <input className={inputClass} value={omschrijving} maxLength={200} onChange={(e) => setOmschrijving(e.target.value)} placeholder={t('plan.omschrijvingPlaceholder', 'Bijv. Lobby Hotel X')} />
        </label>
      </div>
      {locatie !== 'ONLINE' && (
        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex items-center gap-2">
          <Car className="w-4 h-4" />
          {t('plan.reisbufferWaarschuwing', 'Bij een fysieke sessie blokkeert het systeem automatisch reisbuffers voor en na.')}
        </p>
      )}
      <p className="flex items-start gap-2 text-xs text-gray-600 bg-navy-50 border border-navy-100 rounded-md p-2">
        <ShieldCheck className="w-4 h-4 text-navy-700 flex-shrink-0 mt-0.5" />
        {t('matrix.padA.protocol', 'De afspraak staat gestreept tot de medewerker in de eigen omgeving akkoord geeft; daarna wordt hij effen blauw.')}
      </p>
      <div className="flex justify-end">
        <Button onClick={() => plan.mutate()} loading={plan.isPending} disabled={!dossierId}>
          {t('matrix.opslaan.medewerker', 'Voorstel versturen')}
        </Button>
      </div>
    </div>
  );
};

// ── Actie-pad B en C: corporate overleg (goud) ───────────────────────────────

const CorporateFormulier: React.FC<{ van: string; tot: string; geldig: boolean; onKlaar: () => void }> = ({ van, tot, geldig, onKlaar }) => {
  const { t } = useTranslation('agenda');
  const [pad, setPad] = useState<'B' | 'C'>('B');
  const [employerId, setEmployerId] = useState('');
  const [contactId, setContactId] = useState('');
  const [email, setEmail] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [titel, setTitel] = useState('');
  const [soort, setSoort] = useState<B2bSoort>('meeting');
  const [locatie, setLocatie] = useState<B2bLocatie>('online');
  const [omschrijving, setOmschrijving] = useState('');

  const { data: werkgevers = [], isLoading } = useQuery({ queryKey: ['professional-b2b-werkgevers'], queryFn: getB2bWerkgevers });
  const { data: contacten = [], isFetching: contactenLaden } = useQuery({
    queryKey: ['agenda-contactpersonen', employerId],
    queryFn: () => agenda.getContactpersonen(employerId),
    enabled: pad === 'B' && Boolean(employerId),
  });

  const basis = () => ({
    employer_id: employerId,
    soort,
    titel: titel.trim(),
    omschrijving: omschrijving.trim() || null,
    start_tijd: new Date(van).toISOString(),
    eind_tijd: new Date(tot).toISOString(),
    locatie_type: locatie,
  });

  const plan = useMutation({
    mutationFn: async () => {
      if (pad === 'B') {
        await planB2bAfspraakAlsProfessional({ ...basis(), contactpersoon_user_id: contactId || null });
        return null;
      }
      return agenda.nodigExternUit({ ...basis(), external_email: email.trim(), external_telefoon: telefoon.trim() || null });
    },
    onSuccess: (r) => {
      if (pad === 'B') toast.success(t('matrix.gepland.b2b', 'Overleg ingepland — wacht op bevestiging door de opdrachtgever'));
      else if (r && !r.verzonden.email && !r.verzonden.sms) toast(t('matrix.gepland.externNietVerzonden', 'Afspraak aangemaakt, maar de uitnodiging kon niet worden verzonden.'), { icon: '⚠️' });
      else toast.success(t('matrix.gepland.extern', 'Beveiligde uitnodiging verzonden — wacht op bevestiging'));
      onKlaar();
    },
    onError: (err) => toast.error(fout(err, t('fout.boeken', 'Inplannen mislukt'))),
  });

  const geldigC = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const kanOpslaan = geldig && Boolean(employerId) && titel.trim().length >= 3 && (pad === 'B' || geldigC);

  if (isLoading) return <p className="text-sm text-gray-500">{t('matrix.laden', 'Laden…')}</p>;
  if (werkgevers.length === 0) {
    return (
      <p className="text-sm text-gray-600 border-t border-gray-100 pt-3">
        {t('matrix.padB.geenPools', 'Er is nog geen actieve pool met vastgelegde afspraken (functie en tarief). Zonder opdrachtgever kan een overleg niet worden gedeclareerd.')}
      </p>
    );
  }

  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
        <button type="button" onClick={() => setPad('B')} className={`px-3 py-1.5 ${pad === 'B' ? 'bg-navy-900 text-white' : 'bg-white text-gray-700'}`}>
          {t('matrix.padB.tab', 'Bestaande contactpersoon')}
        </button>
        <button type="button" onClick={() => setPad('C')} className={`px-3 py-1.5 ${pad === 'C' ? 'bg-navy-900 text-white' : 'bg-white text-gray-700'}`}>
          {t('matrix.padC.tab', 'Nieuw / extern contact')}
        </button>
      </div>

      <label className="block text-sm">
        <span className="text-gray-700">
          {pad === 'B'
            ? t('matrix.padB.pool', 'Selecteer bedrijfspool')
            : t('matrix.padC.stap1', 'Stap 1 — Selecteer de financiële opdrachtgever / corporate pool')}
        </span>
        <select
          className={inputClass}
          value={employerId}
          onChange={(e) => {
            setEmployerId(e.target.value);
            setContactId('');
          }}
        >
          <option value="">{t('matrix.padB.kiesPool', '— kies een organisatie —')}</option>
          {werkgevers.map((w) => (
            <option key={w.employer_id} value={w.employer_id}>{w.organisatie}</option>
          ))}
        </select>
      </label>

      {pad === 'B' ? (
        <label className="block text-sm">
          <span className="text-gray-700">{t('matrix.padB.contact', 'Selecteer contactpersoon')}</span>
          <select className={inputClass} value={contactId} disabled={!employerId || contactenLaden} onChange={(e) => setContactId(e.target.value)}>
            <option value="">{t('matrix.padB.kiesContact', '— de organisatie (hoofdaccount) —')}</option>
            {contacten.map((c) => (
              <option key={c.user_id} value={c.user_id}>
                {c.naam}
                {c.afdeling ? ` · ${c.afdeling}` : ''}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-gray-700">{t('matrix.padC.email', 'Stap 2 — E-mailadres genodigde')}</span>
            <input type="email" className={inputClass} value={email} disabled={!employerId} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">{t('matrix.padC.telefoon', 'Telefoonnummer genodigde (optioneel, voor SMS)')}</span>
            <input type="tel" className={inputClass} value={telefoon} disabled={!employerId} onChange={(e) => setTelefoon(e.target.value)} maxLength={30} />
          </label>
        </div>
      )}

      <label className="block text-sm">
        <span className="text-gray-700">{t('matrix.padB.titel', 'Onderwerp')}</span>
        <input className={inputClass} value={titel} maxLength={255} onChange={(e) => setTitel(e.target.value)} placeholder={t('matrix.padB.titelPlaceholder', 'Bijv. Kwartaalevaluatie verzuim')} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="text-gray-700">{t('matrix.padB.soort', 'Soort')}</span>
          <select className={inputClass} value={soort} onChange={(e) => setSoort(e.target.value as B2bSoort)}>
            {(Object.keys(B2B_SOORT_LABEL) as B2bSoort[]).map((s) => (
              <option key={s} value={s}>{B2B_SOORT_LABEL[s]}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">{t('plan.locatie', 'Locatie')}</span>
          <select className={inputClass} value={locatie} onChange={(e) => setLocatie(e.target.value as B2bLocatie)}>
            {LOCATIE_B2B.map((l) => (
              <option key={l} value={l}>{t(`matrix.b2bLocatie.${l}`, { online: 'Online (videolink)', telefonisch: 'Telefonisch', op_locatie: 'Op locatie' }[l])}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-sm">
        <span className="text-gray-700">{t('matrix.padB.omschrijving', 'Omschrijving (optioneel)')}</span>
        <textarea className={inputClass} rows={2} maxLength={2000} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} placeholder={t('matrix.padB.omschrijvingPlaceholder', 'Organisatorisch — geen namen of medische gegevens van medewerkers')} />
      </label>
      {locatie === 'op_locatie' && (
        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex items-center gap-2">
          <Car className="w-4 h-4" />
          {t('matrix.padB.reistijd', 'Reistijd voor en na volgt uit de afstand tussen je kantoor en de vestiging van deze opdrachtgever.')}
        </p>
      )}
      <p className="flex items-start gap-2 text-xs text-gray-600 bg-gold-50 border border-gold-200 rounded-md p-2">
        <ShieldCheck className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" />
        {pad === 'B'
          ? t('matrix.padB.protocol', 'Gestreept goud tot de opdrachtgever bevestigt; dan effen goud met videolink. Uren gaan pas na fiat mee in de maandfactuur.')
          : t('matrix.padC.protocol', 'De genodigde krijgt een beveiligde BeLagom-link en bevestigt met één klik. Tarief en facturatie vallen onder de gekozen opdrachtgever, die de uren na afloop fiatteert.')}
      </p>
      <div className="flex justify-end">
        <Button onClick={() => plan.mutate()} loading={plan.isPending} disabled={!kanOpslaan}>
          {pad === 'B' ? t('matrix.opslaan.b2b', 'Overleg inplannen') : t('matrix.opslaan.extern', 'Uitnodiging versturen')}
        </Button>
      </div>
    </div>
  );
};

// ── Netwerk (mos), afwezigheid (zand) en privé (paars) ───────────────────────

const BlokkadeFormulier: React.FC<{ soort: BlokkadeSoort; van: string; tot: string; geldig: boolean; onKlaar: () => void }> = ({
  soort: beginSoort,
  van,
  tot,
  geldig,
  onKlaar,
}) => {
  const { t } = useTranslation('agenda');
  const [soort, setSoort] = useState<BlokkadeSoort>(beginSoort);
  const [omschrijving, setOmschrijving] = useState('');
  useEffect(() => setSoort(beginSoort), [beginSoort]);

  const maak = useMutation({
    mutationFn: () =>
      agenda.maakSoortBlokkade({ soort, start_tijd: new Date(van).toISOString(), eind_tijd: new Date(tot).toISOString(), omschrijving: omschrijving.trim() || null }),
    onSuccess: (r) => {
      toast.success(t('blokkade.toegevoegd', { count: r.geblokkeerde_slots, defaultValue: 'Tijd geblokkeerd ({{count}} tijdslot(s) afgeschermd).' }));
      if (r.conflicten > 0) toast(t('blokkade.conflict', { count: r.conflicten, defaultValue: 'Let op: in deze periode staan al {{count}} afspraak/afspraken.' }), { icon: '⚠️' });
      onKlaar();
    },
    onError: (err) => toast.error(fout(err, t('blokkade.fout', 'Blokkeren mislukt.'))),
  });

  const afwezig = soort === 'VAKANTIE' || soort === 'ZIEK';
  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      {afwezig && (
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
          {(['VAKANTIE', 'ZIEK'] as const).map((s) => (
            <button key={s} type="button" onClick={() => setSoort(s)} className={`px-3 py-1.5 ${soort === s ? 'bg-navy-900 text-white' : 'bg-white text-gray-700'}`}>
              {s === 'VAKANTIE' ? t('matrix.afwezig.vakantie', '🌴 Vakantie / vrij') : t('matrix.afwezig.ziek', '🤒 Ziek')}
            </button>
          ))}
        </div>
      )}
      <label className="block text-sm">
        <span className="text-gray-700">
          {soort === 'NETWERK' ? t('matrix.blokkade.titelNetwerk', 'Titel (bijv. netwerklunch met …)') : t('blokkade.omschrijving', 'Omschrijving (alleen voor jou)')}
        </span>
        <input className={inputClass} value={omschrijving} maxLength={200} onChange={(e) => setOmschrijving(e.target.value)} />
      </label>
      <p className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
        <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
        {afwezig
          ? t('matrix.blokkade.afwezigUitleg', 'Open tijd in deze periode sluit en je staat in je pools op afwezig: medewerkers zien de andere beschikbare professionals uit dezelfde pool. Langer dan 31 dagen? Leg meerdere periodes vast.')
          : t('matrix.blokkade.priveUitleg', 'Open tijd in deze periode sluit. Medewerkers en opdrachtgevers zien hooguit dat je niet beschikbaar bent — nooit de titel of notitie.')}
      </p>
      <div className="flex justify-end">
        <Button onClick={() => maak.mutate()} loading={maak.isPending} disabled={!geldig}>
          {t('matrix.opslaan.blokkade', 'Vastleggen')}
        </Button>
      </div>
    </div>
  );
};
